import { Eye, Edit2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PlanPackage } from '../types';
import { StatusBadge, formatCurrency } from './StatusBadge';

interface PlanTableProps {
    plans: PlanPackage[];
    loading: boolean;
    onView: (plan: PlanPackage) => void;
    onEdit: (plan: PlanPackage) => void;
    onDelete: (plan: PlanPackage) => void;
}

export const PlanTable = ({ plans, loading, onView, onEdit, onDelete }: PlanTableProps) => {
    const { t } = useTranslation();

    const headers = [
        t('plan.table.name'),
        t('plan.table.status'),
        t('plan.table.price'),
        t('plan.table.profit'),
        t('plan.table.duration'),
        t('plan.table.commissionF1'),
        t('plan.table.slot'),
        t('plan.table.actions'),
    ];

    return (
        <div className="overflow-x-auto relative min-h-[200px]">
            {loading && (
                <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 z-10 flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                        {headers.map((header) => (
                            <th
                                key={header}
                                scope="col"
                                className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
                            >
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {!loading && plans.length === 0 && (
                        <tr>
                            <td colSpan={8} className="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                                {t('plan.table.noData')}
                            </td>
                        </tr>
                    )}
                    {plans.map((plan) => (
                        <tr key={plan.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-gray-900 dark:text-white">{plan.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <StatusBadge isActive={plan.is_active} />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                    {formatCurrency(plan.price)}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-white">
                                    {parseFloat(plan.min_profit_rate) > 0 ? (
                                        <span className="font-medium">{parseFloat(plan.min_profit_rate).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}{t('plan.table.perMonth')}</span>
                                    ) : (
                                        <span>{t('plan.table.byRevenue')}</span>
                                    )}
                                </div>
                                {parseFloat(plan.high_profit_rate) > 0 && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {t('plan.table.high')}: {parseFloat(plan.high_profit_rate).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}% ({plan.high_rate_duration} {t('plan.table.firstMonths')})
                                    </div>
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-white">
                                    {plan.duration_months === 0 ? t('plan.table.untilCancel') : `${plan.duration_months} ${t('plan.table.months')}`}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                    {parseFloat(plan.commission_rate).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}%
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {t('plan.table.fromMonth')} {plan.commission_start_month}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-white">
                                    {plan.sold_slots || 0}/{plan.total_slots}
                                </div>
                                <div className="w-20 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full mt-1">
                                    <div
                                        className="h-full bg-blue-600 rounded-full"
                                        style={{ width: `${((plan.sold_slots || 0) / plan.total_slots) * 100}%` }}
                                    />
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => onView(plan)}
                                        className="text-blue-500 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 p-1.5 rounded-md transition-colors"
                                        title={t('plan.table.viewDetail')}
                                    >
                                        <Eye className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => onEdit(plan)}
                                        className="text-amber-500 hover:text-amber-700 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 p-1.5 rounded-md transition-colors"
                                        title={t('plan.table.edit')}
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(plan)}
                                        className="text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 p-1.5 rounded-md transition-colors"
                                        title={t('plan.table.delete')}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
